import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Shared service for efileAdmin components.
 * Replicates efileFactory (EfileDataAdminService) and JsonObjectFactory.saveJSON patterns.
 * See EFILE_ADMIN_MIGRATION_ANALYSIS.md for action_id mapping.
 */
@Injectable({
  providedIn: 'root'
})
export class EfileAdminService {

  constructor(private http: HttpClient) {}

  /**
   * Save via saveJsonObject with action_id (application/x-www-form-urlencoded).
   */
  saveJsonObject(
    baseUrl: string,
    actionId: number,
    params: Record<string, any>,
    globalService?: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=${actionId}`;
    const merged = { ...(globalService?.globalParams || {}), ...(filterParams || {}), ...params };
    const urlEncoded = new URLSearchParams();
    Object.keys(merged).forEach(key => {
      if (merged[key] !== undefined && merged[key] !== null) {
        urlEncoded.set(key, String(merged[key]));
      }
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Load JSON via loadJsonObject (for LOV/dropdown data). Uses GET with params.
   * Merges globalService.globalParams (client_key, scenario, jcd_key, ...) the same
   * way saveJsonObject does, so LOV endpoints that scope by client_key return data.
   */
  getJsonObject(baseUrl: string, params: Record<string, any>, globalService?: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    const merged = { ...(globalService?.globalParams || {}), ...params };
    const httpParams: Record<string, string> = {};
    Object.keys(merged).forEach(key => {
      if (merged[key] !== undefined && merged[key] !== null) {
        httpParams[key] = String(merged[key]);
      }
    });
    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  /**
   * Load forms for efile diagnostic config (action_id 33482).
   * Caller passes params from filterParams + globalParams (tax_year, scenario, screen_key, jcd_key, etc.); no hardcoded values.
   */
  getFormsForEfileDiagnosticConfig(baseUrl: string, params: Record<string, any> = {}, globalService?: any): Observable<any> {
    const merged = { action_id: 33482, ...params };
    return this.getJsonObject(baseUrl, merged, globalService);
  }

  /**
   * Load reverse accounts for efile diagnostic config (action_id 33484).
   * Caller passes same context params as getFormsForEfileDiagnosticConfig plus form; no hardcoded values.
   */
  getReverseAcctForEfileDiagnosticConfig(baseUrl: string, form: string, params: Record<string, any> = {}, globalService?: any): Observable<any> {
    const merged = { action_id: 33484, form, ...params };
    return this.getJsonObject(baseUrl, merged, globalService);
  }

  /**
   * IRS Submission save - uses gtw_efile_api_url, JSON body.
   */
  saveIrsSubmission(efileApiUrl: string, data: any): Observable<any> {
    const url = `${efileApiUrl}/saveIrsSubmission`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    return this.http.post<any>(url, JSON.stringify(data), { headers, withCredentials: true });
  }

  /**
   * Bulk XML Sync - GET to bulk_pdf_engine_api_url.
   */
  bulkSyncXML(bulkPdfEngineUrl: string, taxYear: string, scenario: string, jcdKey: number): Observable<any> {
    const url = `${bulkPdfEngineUrl}/copyFederalXMLtoS3?taxYear=${taxYear}&scenario=${scenario}&jcdKey=${jcdKey}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getFilingGroups(baseUrl: string, taxYear: string, scenario: string, source: string, ssoId: string, state: string = 'FEDERAL'): Observable<any> {
    const jcdKey = source === 'FEDERAL' ? 250 : 253;
    const params = new HttpParams({
      fromObject: {
        action_code: source === 'FEDERAL' ? 'wg95wx' : 'rg0zmc',
        sso_id: ssoId,
        tax_year: taxYear,
        scenario,
        jcd_key: String(jcdKey),
        content_source: source,
        state
      }
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getTaxPayers(baseUrl: string, taxYear: string, scenario: string, source: string, ssoId: string, filingGroup: string): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        action_code: 'x15pkc',
        sso_id: ssoId,
        tax_year: taxYear,
        scenario,
        jcd_key: '250',
        content_source: source,
        state: 'FEDERAL',
        filing_grp: filingGroup
      }
    });
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params, withCredentials: true });
  }

  getSystems(adminUrl: string, taxYear: string, scenario: string, taxPayer: string, fedState: string): Observable<any> {
    const jcdKey = fedState === 'FEDERAL' ? '250' : '253';
    const params = new HttpParams({
      fromObject: {
        tax_year: taxYear,
        scenario,
        jcd_key: jcdKey,
        tax_payer: fedState === 'FEDERAL' ? taxPayer : ''
      }
    });
    return this.http.post<any>(`${adminUrl}/efile/getAllSystems`, null, { params, withCredentials: true });
  }

  pushData(adminUrl: string, paramsObj: Record<string, any>): Observable<any> {
    const params = new HttpParams({ fromObject: this.toHttpParamObject(paramsObj) });
    return this.http.post<any>(`${adminUrl}/efile/pushEfileData`, null, { params, withCredentials: true });
  }

  generatePDFOnly(adminUrl: string, paramsObj: Record<string, any>): Observable<any> {
    const params = new HttpParams({ fromObject: this.toHttpParamObject(paramsObj) });
    return this.http.post<any>(`${adminUrl}/efile/generatePDFOnly`, null, { params, withCredentials: true });
  }

  generateXMLOnly(adminUrl: string, paramsObj: Record<string, any>): Observable<any> {
    const params = new HttpParams({ fromObject: this.toHttpParamObject(paramsObj) });
    return this.http.post<any>(`${adminUrl}/efile/generateXMLsOnly`, null, { params, withCredentials: true });
  }

  refreshView(adminUrl: string, filingType: string): Observable<any> {
    const params = new HttpParams({ fromObject: { type: filingType } });
    return this.http.post<any>(`${adminUrl}/efile/mViewRefresh`, null, { params, withCredentials: true });
  }

  /**
   * XML vs Elements Comparison - GET to gtw_efile_api_url.
   */
  xmlVsElementsCompare(efileApiUrl: string, irsSubmissionId: string): Observable<any> {
    const url = `${efileApiUrl}/validateSubmissionXml?irsSubmissionId=${irsSubmissionId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  private toHttpParamObject(paramsObj: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    Object.keys(paramsObj || {}).forEach((key) => {
      out[key] = String(paramsObj[key] ?? '');
    });
    return out;
  }
}
